import { Control, useController, FieldPath } from 'react-hook-form';
import { AceGenericFieldLayout } from './AceGenericFieldLayout';
import { ReactNode, useState } from 'react';
import { NumericInput } from '@blueprintjs/core';
import { defaultFormatter } from './common';
import styles from './AceNumberField.module.css';

interface AceNumberFieldProps<T extends object, V> {
  control: Control<T>;
  name: FieldPath<T>;
  formatter?: (value: V) => ReactNode;
  editable?: boolean;
  label?: ReactNode;
  vertical?: boolean;
}

function toDisplayString(value: unknown): string {
  const num = value as number | null | undefined;
  return num != null && !isNaN(num) ? String(num) : '';
}

export function AceNumberField<T extends object, V>({
  control,
  name,
  editable,
  label,
  formatter = defaultFormatter,
  vertical,
}: AceNumberFieldProps<T, V>) {
  const { field, fieldState } = useController({ control, name });

  const [{ displayValue, prevFieldValue }, setDisplayState] = useState(() => ({
    displayValue: toDisplayString(field.value),
    prevFieldValue: field.value as number | null | undefined,
  }));

  // Sync display value on external field changes (e.g. form reset) without
  // overwriting in-progress input. Object.is handles the NaN === NaN case.
  if (!Object.is(prevFieldValue, field.value as number | null | undefined)) {
    setDisplayState({
      displayValue: toDisplayString(field.value),
      prevFieldValue: field.value as number | null | undefined,
    });
  }

  const formattedValue = formatter(field.value as V);

  return (
    <AceGenericFieldLayout
      editable={editable}
      label={label}
      error={fieldState.error?.message}
      vertical={vertical}
      valueElement={formattedValue}
      inputElement={
        <NumericInput
          className={styles.inputGroup}
          name={field.name}
          onValueChange={(valueAsNumber, valueAsString) => {
            setDisplayState({
              displayValue: valueAsString,
              prevFieldValue: valueAsNumber,
            });
            field.onChange(valueAsNumber);
          }}
          onBlur={field.onBlur}
          value={displayValue}
          allowNumericCharactersOnly={true}
          inputRef={field.ref}
          fill
          buttonPosition="none"
        />
      }
    />
  );
}
